import { useEffect } from "react";
import { useForm, validators, useFormSubmission } from "@hooks";
import { type Location, useLocation, useNavigate } from 'react-router-dom';
import { useSpinnerContext } from '@contexts';
import { sunboundPayProxy } from '@proxies';
import { helpers } from "@shared";
import { Button, TextField } from "@common";
import { PostHogEvents, capturePosthogEvent } from '@analytics';

type State = {
    email: string;
};

const ForgotPassword = () => {

    const navigate = useNavigate();
    const location = useLocation() as Location<State | null>;
    const email = location.state?.email ?? ``;
    const initialState = { email, password: `` };
    const { setSpinnerShow } = useSpinnerContext();

    const { setFormState } = useFormSubmission();

    const validations = {
        email: (val: string) => validators.isValidEmail(val) || { email: `Please use a valid email address.` }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const submit = async () => {
        setSpinnerShow(true);
        try {
            const { email } = values;
            await sunboundPayProxy.sendPasswordReset(email);
            capturePosthogEvent(PostHogEvents.PASSWORD_RESET_REQUESTED, { email });
            helpers.navigateNext(location, navigate, undefined, { email });

        } catch (error) {
            setFormState(
                error instanceof Error
                    ? `Password reset failed. ${error.message}`
                    : `Password reset failed.`
            );
        }
        setSpinnerShow(false);
    };

    const { values, errors, changeHandler, submitHandler } = useForm(initialState, {}, validations, submit);

    const isFormSubmittable = values.email && values.email.trim().length > 0;

    return (
        <div className="flex flex-col w-full">
            <div className="border-b border-neutral-100 pb-6 mb-6 md:mb-8">
                <h1 className="text-[20px] leading-[24px] md:text-[28px] md:leading-[40px] font-medium text-neutral-950 tracking-[0.2px] md:tracking-[0.28px]">Forgot Password</h1>
            </div>
            <p className="text-sm leading-5 text-neutral-800 tracking-[0.14px] mb-6 md:mb-8">
                Enter your email to receive a link to reset your password.
            </p>
            <form method="POST" className="w-full flex flex-col gap-8" onSubmit={submitHandler} >
                <TextField
                    type="email"
                    name="email"
                    placeholder="Email*"
                    value={values.email}
                    onChange={changeHandler}
                    error={errors.email}
                />
                <div className="flex flex-col gap-4 items-center w-full">
                    <Button
                        type="submit"
                        variant="primary"
                        size="lg"
                        className="w-full"
                        disabled={!isFormSubmittable}
                    >
                        Reset Password
                    </Button>
                    <Button
                        variant="link"
                        size="sm"
                        onClick={() => navigate(`/login`)}
                    >
                        Back to Login
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default ForgotPassword;
